import React from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';
import {NextSeo} from 'next-seo';
import {Button, BUTTON_THEMES, LoaderComponent} from 'ht-styleguide';

/* Components */
import {DevlinkAllServicesHero} from '@devlink/DevlinkAllServicesHero';
import SpaceY from '@components/UI/Space/Space.y-axis';
import Maybe from '@components/Maybe';
import LazyComponent from '@components/LazyComponent';
import {DevlinkSkuCardMinimal} from '@devlink/DevlinkSkuCardMinimal';

/* Global State */
import {useResponsiveIsMobile} from '@store/client/store.responsive';

/* Constants / Utils */
import {faqPath, plansPath} from '@constants/paths';
import {prevented} from '@utils/event';
import {DEFAULT_ALT_HERO_IMAGE} from '@scenes/LandingsPage/landingspage.constants';
import {SCROLL_ID, META_DESCRIPTION, META_TITLE} from '@scenes/ServicesPage/servicespage.constants';
import {THEME_TYPES} from '@constants/theme';
import {smoothScrollTo} from '@utils/helpers';

/* Types */
import {TCategories, TPopularSkus, TServicesPageProps} from '@scenes/ServicesPage/servicespage.types';

/* Styles */
import baseStyles from '@components/base.module.scss';
import styles from './servicespage.module.scss';

/* Dynamic Componnts */
const DevlinkValuePropStripDynamic = dynamic(() => import('@devlink/DevlinkValuePropStrip').then(module => module.DevlinkValuePropStrip), {
  ssr: false,
});
const DevLinkSkuGrid4ColumnsDynamic = dynamic(() => import('@devlink/DevlinkSkuGrid4Columns').then(module => module.DevlinkSkuGrid4Columns), {
  ssr: false,
});
const DevlinkSkuCategorySelectProminentSectionDynamic = dynamic(
  () => import('@devlink/DevlinkSkuCategorySelectProminentSection').then(module => module.DevlinkSkuCategorySelectProminentSection),
  {
    ssr: false,
  },
);
const DevlinkHowItWorksDynamic = dynamic(() => import('@devlink/DevlinkHowItWorks').then(module => module.DevlinkHowItWorks), {
  ssr: false,
});
const DevlinkPartnerLogosDynamic = dynamic(() => import('@devlink/DevlinkPartnerLogos').then(module => module.DevlinkPartnerLogos), {
  ssr: false,
});
const DevlinkPeaceOfMindBlockDynamic = dynamic(() => import('@devlink/DevlinkPeaceOfMindBlock').then(module => module.DevlinkPeaceOfMindBlock), {
  ssr: false,
});
const DevlinkProductSelectionSectionDynamic = dynamic(
  () => import('@devlink/DevlinkProductSelectionSection').then(module => module.DevlinkProductSelectionSection),
  {
    ssr: false,
  },
);
const DevlinkSectionDynamic = dynamic(() => import('@devlink/DevlinkSection').then(module => module.DevlinkSection), {
  ssr: false,
});
const DevlinkContentBlockCenterDynamic = dynamic(
  () => import('@devlink/DevlinkContentBlockCenter').then(module => module.DevlinkContentBlockCenter),
  {
    ssr: false,
  },
);
const DevlinkskuCardMinProminentDynamic = dynamic(
  () => import('@devlink/DevlinkSkuCardMinProminent').then(module => module.DevlinkSkuCardMinProminent),
  {ssr: false},
);
const CustomerReviewsDynamic = dynamic(() => import('@components/UI/Reviews/CustomerReviews'), {ssr: false});
const HeroSearchbarDynamic = dynamic(() => import('@components/Searchbar/Hero'), {
  loading: () => (
    <div style={{paddingBottom: '15px'}}>
      <LoaderComponent />
    </div>
  ),
});

const AllServicesPage = ({categories, popularSkus, reviews}: TServicesPageProps) => {
  /* Hooks */
  const router = useRouter();

  /* Client Store */
  const isMobile = useResponsiveIsMobile();

  /* Methods */
  /**
   * Render Minimal Cards for popular services
   * @param {TPopularSkus[]} skus
   * @returns {React.JSX.Element[]}
   */
  const renderMinimalCards = (skus: TPopularSkus[]): React.JSX.Element[] =>
    skus.map(sku => (
      <DevlinkSkuCardMinimal
        skuName={sku.name}
        showSkuIcon={false}
        showSkuIconSlot={true}
        skuIconSlot={<Image src={sku.image} alt={`Expert service & support for ${sku.name}`} layout="responsive" width={48} height={48} />}
        action={{onClick: prevented(() => router.push(sku?.link))}}
        key={sku.name}
      />
    ));

  /**
   * Render Prominent Cards panels for categories
   * @param {TCategories[]} cats
   * @returns {React.JSX.Element[]}
   */
  const renderProminentCards = (cats: TCategories[]): React.JSX.Element[] =>
    cats.map(cat => (
      <DevlinkskuCardMinProminentDynamic
        imageShowImageSource={false}
        title={cat.name}
        imageShowImageSlot={true}
        imageImageSlot={
          <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <Image
              src={cat.image}
              alt={`Expert service & support for ${cat.name}`}
              fill
              quality={65}
              style={{
                objectFit: 'contain',
              }}
            />
          </div>
        }
        action={{onClick: prevented(() => router.push(cat?.link))}}
        key={cat.name}
      />
    ));

  /**
   * Render Chips
   * @param {TCategories[]} c
   * @returns {React.JSX.Element[]}
   */
  const renderChips = (c: TCategories[]): React.JSX.Element[] =>
    c.map(category => (
      <div className={styles.chip} onClick={() => router.push(category?.link)}>
        {category.name}
      </div>
    ));

  return (
    <>
      <NextSeo title={META_TITLE} description={META_DESCRIPTION} />
      <DevlinkAllServicesHero
        heroTitle="A helping hand for all your home tech"
        search={<HeroSearchbarDynamic theme={THEME_TYPES.SUCCESS} segmentLocation="services search" />}
        categoryChips={renderChips(categories)}
        imageSlot={
          <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <Image
              src="/_nextassets_/images/hero/allservices-hero-img.jpg"
              style={{
                objectFit: 'cover',
              }}
              layout="fill"
              quality={65}
              priority
              alt={DEFAULT_ALT_HERO_IMAGE}
            />
          </div>
        }
        showImageSource={false}
      />
      <SpaceY classes="paddingTop-large2" />
      <div id={SCROLL_ID} />
      <LazyComponent minHeight={isMobile ? '790px' : '468px'}>
        <DevlinkSectionDynamic
          sectionBody={
            <DevLinkSkuGrid4ColumnsDynamic
              displayGridTitle={false}
              displayGridProminentTitle
              gridTitle="Popular Services"
              skuGrid={renderMinimalCards(popularSkus)}
              gridTitleStyle={{styles: {color: 'htTealGreen'}}}
            />
          }
        />
      </LazyComponent>
      <SpaceY classes="paddingTop-large2" />
      <LazyComponent minHeight={isMobile ? '210px' : '82px'}>
        <DevlinkValuePropStripDynamic />
      </LazyComponent>
      <SpaceY classes="paddingTop-large2" />
      <LazyComponent minHeight={isMobile ? '1364px' : '776px'}>
        <DevlinkSkuCategorySelectProminentSectionDynamic categoryItems={renderProminentCards(categories)} />
      </LazyComponent>
      <SpaceY classes="paddingTop-huge1" />
      <LazyComponent minHeight={isMobile ? '1200px' : '570px'}>
        <DevlinkHowItWorksDynamic
          showButton
          buttonSlot={
            <Button onClick={() => smoothScrollTo({pos: 50})} className={baseStyles.basicButton} theme={BUTTON_THEMES.PRIMARY}>
              Browse Services
            </Button>
          }
        />
      </LazyComponent>
      <SpaceY classes="paddingTop-huge1" />
      <SpaceY classes="paddingTop-medium" />
      <LazyComponent minHeight={isMobile ? '116px' : '112px'}>
        <DevlinkPartnerLogosDynamic />
      </LazyComponent>
      <SpaceY classes="paddingTop-medium" />
      <SpaceY classes="paddingTop-huge1" />
      <LazyComponent minHeight={isMobile ? '220px' : '475px'}>
        <DevlinkPeaceOfMindBlockDynamic />
      </LazyComponent>
      <Maybe condition={reviews?.length > 2}>
        <SpaceY classes="paddingTop-huge1" />
        <SpaceY classes="paddingTop-medium" />
        <LazyComponent minHeight={isMobile ? '250px' : '330px'}>
          <CustomerReviewsDynamic
            headerText="We’ve helped and satisfied 500,000+ homes nationwide"
            className="paddingX-medium"
            reviews={reviews}
            headerStyles="text-align-center"
            removeBackground={true}
            addShadow={false}
          />
        </LazyComponent>
      </Maybe>
      <SpaceY classes="paddingTop-huge1" />
      <LazyComponent minHeight={isMobile ? '944px' : '844px'}>
        <DevlinkProductSelectionSectionDynamic
          serviceAction={{onClick: prevented(() => smoothScrollTo({pos: 50}))}}
          membershipAction={{onClick: prevented(() => router.push(plansPath()))}}
        />
      </LazyComponent>
      <LazyComponent minHeight={isMobile ? '156px' : '260px'}>
        <DevlinkContentBlockCenterDynamic
          showEyebrowText={false}
          headline="Have any questions?"
          showBodyText={false}
          showButtons={true}
          buttons={
            <Button className={baseStyles.basicButton} theme={BUTTON_THEMES.primary} onClick={prevented(() => router.push(faqPath()))}>
              See FAQs
            </Button>
          }
          hasLightBgBlob={true}
        />
      </LazyComponent>
    </>
  );
};

export default AllServicesPage;
